import { WarningAmberOutlined as WarningAmberOutlinedIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { TreeItemProps } from '@mui/x-tree-view';
import { useField } from 'formik';
import { filter, get, groupBy, isEmpty, keyBy, map } from 'lodash';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikTreeNode, FormikTreeProps, FormikTree } from 'components';
import { useCurrencies, useUserContext, useUserContextUtils } from 'hooks';
import { TradeMethod } from 'types';

type Props = Pick<
  FormikTreeProps,
  'fieldName' | 'fieldValueIdPropertyName' | 'additionalItemSettings'
> & {
  tradeMethods?: TradeMethod[];
  fiatCurrenciesIds?: string[];
};

const UnsupportedTradeMethodWarning: React.FC<{
  item: TreeItemProps;
  tradeMethodsMap: Record<string, TradeMethod>;
}> = ({ item, tradeMethodsMap }) => {
  const { t: tCommon } = useTranslation();
  return !isEmpty(item.children) ||
    tradeMethodsMap[item.itemId]?.enabled ? null : (
    <Tooltip
      title={tCommon('features.requisites.error.trade_method_unavailable')}
    >
      <WarningAmberOutlinedIcon
        color="warning"
        sx={{
          width: 20,
          height: 20,
          marginLeft: '10px',
        }}
        fontSize="small"
      />
    </Tooltip>
  );
};

export const TradeMethodTreeSelect: React.FC<Props> = ({
  tradeMethods,
  fiatCurrenciesIds = [],
  fieldName,
  fieldValueIdPropertyName = 'id',
  additionalItemSettings,
}) => {
  const { allTradeMethods } = useUserContext();
  const { getPaymentTypeLabel, getBankName } = useUserContextUtils();
  const { getFiatCurrencyCode } = useCurrencies();

  const [{ value: fieldValue }, , helpers] = useField(fieldName);

  const filteredTradeMethods = useMemo(
    () =>
      filter(
        tradeMethods || allTradeMethods,
        (tradeMethod: TradeMethod) =>
          isEmpty(fiatCurrenciesIds) ||
          fiatCurrenciesIds.includes(tradeMethod.fiatCurrencyId),
      ),
    [allTradeMethods, fiatCurrenciesIds, tradeMethods],
  );

  const tradeMethodsMap: Record<string, TradeMethod> = useMemo(
    () => keyBy(filteredTradeMethods, 'id'),
    [filteredTradeMethods],
  );

  const leafIds = useMemo(
    () => Object.keys(tradeMethodsMap),
    [tradeMethodsMap],
  );

  const defaultSelectedItems = useRef<string[]>([]);
  if (!isEmpty(fieldValue) && isEmpty(defaultSelectedItems.current)) {
    defaultSelectedItems.current = map(fieldValue, (fieldValue) =>
      get(fieldValue, fieldValueIdPropertyName),
    );
  }

  const renderExtraLabelContent = useCallback(
    (item: TreeItemProps) => (
      <UnsupportedTradeMethodWarning
        item={item}
        tradeMethodsMap={tradeMethodsMap}
      />
    ),
    [tradeMethodsMap],
  );

  const getItemDefaultValue = useCallback(
    (itemId: string) => ({
      [fieldValueIdPropertyName]: itemId,
      ...(additionalItemSettings?.defaultValues || {}),
    }),
    [additionalItemSettings?.defaultValues, fieldValueIdPropertyName],
  );

  const handleSelectedItemsChange = useCallback(
    (e: React.SyntheticEvent | null, selectedLeafIds: string[]) => {
      const prevSelectedItemIds = keyBy(fieldValue, fieldValueIdPropertyName);
      helpers.setValue(
        map(
          selectedLeafIds,
          (itemId) =>
            prevSelectedItemIds[itemId] || getItemDefaultValue(itemId),
        ),
      );
    },
    [fieldValue, fieldValueIdPropertyName, helpers, getItemDefaultValue],
  );

  const tree = useMemo((): FormikTreeNode[] => {
    const groupedByCurrency: Record<string, TradeMethod[]> = groupBy(
      filteredTradeMethods,
      'fiatCurrencyId',
    );

    return map(
      groupedByCurrency,
      (fiatCurrencyTradeMethods, fiatCurrencyId) => {
        const currencyNode: FormikTreeNode = {
          id: fiatCurrencyId,
          label: getFiatCurrencyCode(fiatCurrencyId),
          children: [],
          path: [fiatCurrencyId],
        };

        const groupedByPaymentType: Record<string, TradeMethod[]> = groupBy(
          fiatCurrencyTradeMethods,
          'paymentTypeId',
        );

        currencyNode.children = map(
          groupedByPaymentType,
          (paymentTypeTradeMethods, paymentTypeId) => {
            const id = `${paymentTypeId}_${fiatCurrencyId}`;
            const paymentTypeNode: FormikTreeNode = {
              id,
              label: getPaymentTypeLabel({ id: paymentTypeId }),
              children: [],
              path: [...currencyNode.path, id],
            };

            const groupedByBank = keyBy(paymentTypeTradeMethods, 'bankId');

            paymentTypeNode.children = map(
              groupedByBank,
              (tradeMethod, bankId) => ({
                id: tradeMethod.id,
                label: getBankName({ id: bankId }) as string,
                children: [],
                path: [...paymentTypeNode.path, tradeMethod.id],
              }),
            );

            return paymentTypeNode;
          },
        );

        return currencyNode;
      },
    );
  }, [
    filteredTradeMethods,
    getBankName,
    getFiatCurrencyCode,
    getPaymentTypeLabel,
  ]);

  return (
    <FormikTree
      fieldName={fieldName}
      fieldValueIdPropertyName={fieldValueIdPropertyName}
      items={tree}
      leafIds={leafIds}
      defaultSelectedItems={defaultSelectedItems.current}
      onChange={handleSelectedItemsChange}
      renderExtraLabelContent={renderExtraLabelContent}
      additionalItemSettings={additionalItemSettings}
    />
  );
};
